<template>
  <component
    :is="element"
    class="media-carousel__slide"
    :href="slideUrl"
  >
    <background-media
      :carousel-index="carouselIndex"
      :element-index="elementIndex"
      :component-settings="{
        contentColor: sectionSettings?.contentColor,
        titleText: slide.titleText,
        titleMobileSize: slide.titleMobileSize,
        titleDesktopSize: slide.titleDesktopSize,
        bodyText: slide.bodyText,
        bodyMobileSize: slide.bodyMobileSize,
        bodyDesktopSize: slide.bodyDesktopSize,
        buttonText: slide.buttonText,
        buttonURL: slide.buttonURL,
        layout: slide.layout,
        hasContent: true,
        hasIndexLayout: true,
        autoplay: true,
        loop: true,
        muted: true,
        type: 'carousel',
      }"
      :content-placement="contentPlacement"
      :mobile-media-settings="slide.mobileMediaSettings"
      :desktop-media-settings="slide.desktopMediaSettings"
      @carousel-image-loaded="$emit('carousel-image-loaded', elementIndex)"
    />
  </component>
</template>

<script>

/**
 * Vue: Carousel slide (carousel-slide)
 * -----------------------------------------------------------------------------
 * Slide component for media-carousel section.
 *
 * @param {Number} carouselIndex - Index of the current slide in the carousel.
 * @param {Number} elementIndex - Index the element is within its parent array.
 * @param {Object} slide - Object containing block settings.
 * @param {Object} sectionSettings - Object containing parent section settings.
 * @param {String} contentPlacement - String indicating if the component is
 * occupying half or full width.
 *
 *
 */
import { mapState } from 'vuex'
import BackgroundMedia from '~async/background-media/background-media.vue'

export default {
  name: 'CarouselSlide',

  components: {
    BackgroundMedia,
  },

  props: {
    carouselIndex: {
      type: Number,
      default: 0,
    },

    contentPlacement: {
      type: String,
      default: 'full',
    },

    elementIndex: {
      type: Number,
      default: 0,
    },

    slide: {
      type: Object,
      default: () => ({
        mobileMediaSettings: {
          mobileDefaultHeight: '',
          mobileVideoFile: '',
          mobileVimeoId: '',
          mobileImage: '',
        },
        desktopMediaSettings: {
          desktopDefaultHeight: '',
          desktopVideoFile: '',
          desktopVimeoId: '',
          desktopImage: '',
        },
        titleText: '',
        titleMobileSize: '',
        titleDesktopSize: '',
        bodyText: '',
        bodyMobileSize: '',
        bodyDesktopSize: '',
        buttonText: '',
        buttonURL: '',
        layout: '',
        mediaLink: '',
      }),
    },

    sectionSettings: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: {
    'carousel-image-loaded': (payload) => typeof payload === 'number',
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute element type.
     * @returns {String}
     */
    element() {
      return this.slide.buttonURL || this.slide.mediaLink ? 'a' : 'div'
    },

    /**
     * Compute element url.
     * @returns {String}
     */
    slideUrl() {
      const hasUrl = this.slide.buttonURL || this.slide.mediaLink

      if (hasUrl) {
        if (this.slide.buttonURL) {
          return this.slide.buttonURL
        }

        if (this.slide.mediaLink) {
          return this.slide.mediaLink
        }
      }

      return null
    },
  },
}
</script>

<style lang="scss">
@import './carousel-slide';
</style>
